import React, { useState, useEffect } from 'react';
import { Move, Trash2, Copy, Edit, GripVertical, Eye, EyeOff, RefreshCw } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { useTenantProtection } from '../../../hooks/useTenantProtection';
import TableSection from './TableSection';
import ChartSection from './ChartSection';
import TextSection from './TextSection';

const ReportCanvas = ({
  sections,
  selectedSection,
  onSelectSection,
  onUpdateSection,
  onDeleteSection,
  onDuplicateSection,
  onPositionChange,
  layoutConfig,
  parameters = []
}) => {
  const { user } = useAuth();
  const { getProtectedTenantCode } = useTenantProtection();
  const [sectionData, setSectionData] = useState({});
  const [loadingSection, setLoadingSection] = useState({});
  const [showPreview, setShowPreview] = useState(true);
  // Refresh section preview data when section config changes
  useEffect(() => {
    if (showPreview) {
      sections.forEach(section => {
        if (section.config.entity && section.config.fields && section.config.fields.length > 0) {
          loadSectionPreview(section);
        }
      });
    }
  }, [sections, showPreview]);

  const loadSectionPreview = async (section) => {
    try {
      setLoadingSection(prev => ({ ...prev, [section.id]: true }));
      const tenantCode = getProtectedTenantCode();
      const token = localStorage.getItem('token');

      // Initialize parameters with defaults
      const previewParams = {};
      if (parameters && parameters.length > 0) {
        parameters.forEach(param => {
          if (param.defaultValue !== undefined && param.defaultValue !== null) {
            previewParams[param.name] = param.defaultValue;
          }
        });
      }

      const response = await fetch('/api/reports/preview-section', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Code': tenantCode,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          section: {
            type: section.type,
            config: {
              ...section.config,
              limit: 5 // Preview only 5 rows
            }
          },
          parameters: previewParams
        })
      });

      if (response.ok) {
        const data = await response.json();
        setSectionData(prev => ({
          ...prev,
          [section.id]: data.data
        }));
      }
    } catch (error) {
      console.error('Error loading section preview:', error);
    } finally {
      setLoadingSection(prev => ({ ...prev, [section.id]: false }));
    }
  };

  const handleSectionClick = (e, sectionId) => {
    e.stopPropagation();
    onSelectSection(sectionId);
  };

  const handleCanvasClick = () => {
    onSelectSection(null);
  };

  const togglePreview = () => {
    setShowPreview(!showPreview);
  };

  const handleMoveUp = (section) => {
    if (section.position.row > 0) {
      onPositionChange(section.id, {
        ...section.position,
        row: section.position.row - 1
      });
    }
  };

  const handleMoveDown = (section) => {
    onPositionChange(section.id, {
      ...section.position,
      row: section.position.row + 1
    });
  };

  const handleResize = (section, change) => {
    const newPosition = { ...section.position };
    
    if (change === 'width-increase' && newPosition.width < 12) {
      newPosition.width += 1;
    } else if (change === 'width-decrease' && newPosition.width > 1) {
      newPosition.width -= 1;
    } else if (change === 'height-increase') {
      newPosition.height += 1;
    } else if (change === 'height-decrease' && newPosition.height > 1) {
      newPosition.height -= 1;
    }
    
    onPositionChange(section.id, newPosition);
  };

  const renderSectionPreview = (section) => {
    const data = sectionData[section.id];
    const isLoading = loadingSection[section.id];

    // If preview is off, show placeholder
    if (!showPreview) {
      return renderPlaceholder(section);
    }

    // If loading, show spinner
    if (isLoading) {
      return (
        <div className="h-full flex items-center justify-center">
          <div className="flex flex-col items-center">
            <RefreshCw className="w-8 h-8 text-blue-500 animate-spin mb-2" />
            <p className="text-sm text-gray-600 dark:text-gray-400">Loading preview...</p>
          </div>
        </div>
      );
    }

    // If no data yet or no configuration, show placeholder
    if (!data || !section.config.entity) {
      return renderPlaceholder(section);
    }

    // Render actual section with data
    const sectionWithData = {
      ...section,
      data: data.data
    };

    switch (section.type) {
      case 'table':
        return (
          <div className="h-full overflow-auto scale-90 origin-top-left" style={{ width: '111%' }}>
            <TableSection section={sectionWithData} />
          </div>
        );
      case 'chart':
        return (
          <div className="h-full overflow-auto scale-90 origin-top-left" style={{ width: '111%' }}>
            <ChartSection section={sectionWithData} />
          </div>
        );
      case 'text':
        return (
          <div className="h-full overflow-auto scale-90 origin-top-left" style={{ width: '111%' }}>
            <TextSection section={sectionWithData} />
          </div>
        );
      default:
        return renderPlaceholder(section);
    }
  };

  const renderPlaceholder = (section) => {
    switch (section.type) {
      case 'table':
        return (
          <div className="h-full flex flex-col p-4">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-3 h-3 bg-blue-400 rounded"></div>
              <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {section.config.title || 'Table Section'}
              </div>
            </div>
            {section.config.entity ? (
              <div className="text-xs text-gray-500 dark:text-gray-400 italic">
                {section.config.fields && section.config.fields.length > 0 
                  ? `Preview with ${section.config.fields.length} fields`
                  : 'Configure fields to see preview'}
              </div>
            ) : (
              <div className="text-xs text-gray-500 dark:text-gray-400 italic">
                Select an entity to see data preview
              </div>
            )}
            <div className="flex-1 grid grid-cols-3 gap-1 mt-3">
              <div className="bg-gray-300 dark:bg-gray-600 h-6 rounded"></div>
              <div className="bg-gray-300 dark:bg-gray-600 h-6 rounded"></div>
              <div className="bg-gray-300 dark:bg-gray-600 h-6 rounded"></div>
              <div className="bg-gray-200 dark:bg-gray-700 h-4 rounded"></div>
              <div className="bg-gray-200 dark:bg-gray-700 h-4 rounded"></div>
              <div className="bg-gray-200 dark:bg-gray-700 h-4 rounded"></div>
            </div>
          </div>
        );
      case 'chart':
        return (
          <div className="h-full flex flex-col items-center justify-center p-4">
            <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {section.config.title || 'Chart Section'}
            </div>
            {section.config.entity ? (
              <div className="text-xs text-gray-500 dark:text-gray-400 italic mb-3">
                {section.config.chartType || 'bar'} chart preview
              </div>
            ) : (
              <div className="text-xs text-gray-500 dark:text-gray-400 italic mb-3">
                Configure chart to see preview
              </div>
            )}
            <div className="flex items-end gap-1 h-16">
              <div className="w-8 bg-blue-400 rounded-t" style={{ height: '60%' }}></div>
              <div className="w-8 bg-blue-500 rounded-t" style={{ height: '80%' }}></div>
              <div className="w-8 bg-blue-400 rounded-t" style={{ height: '40%' }}></div>
              <div className="w-8 bg-blue-500 rounded-t" style={{ height: '100%' }}></div>
            </div>
          </div>
        );
      case 'text':
        return (
          <div className="h-full flex flex-col p-4">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-3 h-3 bg-purple-400 rounded"></div>
              <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {section.config.title || 'Text Section'}
              </div>
            </div>
            {section.config.content ? (
              <div className="text-xs text-gray-700 dark:text-gray-300" dangerouslySetInnerHTML={{ __html: section.config.content.substring(0, 100) + '...' }}></div>
            ) : (
              <div className="text-xs text-gray-500 dark:text-gray-400 italic">
                Add content to see preview
              </div>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  if (sections.length === 0) {
    return (
      <div 
        className="w-full h-full min-h-[500px] bg-white dark:bg-gray-800 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 flex items-center justify-center"
        onClick={handleCanvasClick}
      >
        <div className="text-center">
          <GripVertical className="w-16 h-16 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
            Empty Canvas
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            Add sections from the left panel to start building your report
          </p>
        </div>
      </div>
    );
  }

  // Calculate grid
  const sortedSections = [...sections].sort((a, b) => a.position.row - b.position.row);
  const maxRow = Math.max(...sections.map(s => s.position.row + s.position.height), 5);

  return (
    <div className="w-full min-h-[500px]">
      {/* Preview Toggle */}
      <div className="flex justify-end mb-2">
        <button
          onClick={togglePreview}
          className={`flex items-center gap-2 px-3 py-1 rounded-lg text-sm transition-colors ${
            showPreview
              ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300'
              : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
          }`}
        >
          {showPreview ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
          {showPreview ? 'Live Preview' : 'Design Mode'}
        </button>
      </div>

      {/* A4 Paper Container - WYSIWYG Style */}
      <div className="flex justify-center bg-gray-100 dark:bg-gray-900 rounded-lg p-8 min-h-[500px]">
        <div 
          className="w-full max-w-[210mm] bg-white shadow-lg relative"
          style={{ minHeight: '297mm' }}
          onClick={handleCanvasClick}
        >
          <div className="p-12 space-y-6">
            {sortedSections.map((section, index) => {
              const isSelected = selectedSection === section.id;

              return (
                <div
                  key={section.id}
                  onClick={(e) => handleSectionClick(e, section.id)}
                  className={`relative transition-all duration-200 cursor-pointer ${
                    isSelected
                      ? 'ring-2 ring-blue-500 shadow-lg'
                      : 'hover:ring-1 hover:ring-blue-300'
                  } ${index > 0 ? 'border-t border-gray-200 pt-6' : ''}`}
                  style={{
                    minHeight: `${section.position.height * 100}px`
                  }}
                >
                  {/* Section Toolbar (shows on selection) */}
                  {isSelected && (
                    <div className="absolute -top-10 left-0 right-0 flex items-center justify-between bg-blue-500 text-white px-3 py-1 rounded-t-lg text-sm z-20">
                      <div className="flex items-center gap-2">
                        <span className="font-medium capitalize">{section.type} Section</span>
                        <button
                          onClick={(e) => { e.stopPropagation(); loadSectionPreview(section); }}
                          className="p-1 hover:bg-blue-600 rounded"
                          title="Refresh preview"
                        >
                          <RefreshCw className="w-3 h-3" />
                        </button>
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={(e) => { e.stopPropagation(); handleMoveUp(section); }}
                          disabled={index === 0}
                          className="p-1 hover:bg-blue-600 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Move up"
                        >
                          ↑
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleMoveDown(section); }}
                          disabled={index === sortedSections.length - 1}
                          className="p-1 hover:bg-blue-600 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Move down"
                        >
                          ↓
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); onDuplicateSection(section.id); }}
                          className="p-1 hover:bg-blue-600 rounded"
                          title="Duplicate"
                        >
                          <Copy className="w-3 h-3" />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); onDeleteSection(section.id); }}
                          className="p-1 hover:bg-red-600 rounded"
                          title="Delete"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Section Content */}
                  <div className="overflow-hidden">
                    {renderSectionPreview(section)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportCanvas;


